import { useTranslation } from "react-i18next";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { type FormInstance } from "antd";
import { createStyles } from "antd-style";

import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Modal } from "@av/components/atoms/Modal";
import { Select } from "@av/components/atoms/Select";
import { TooltipLabel } from "@av/components/molecules/TooltipLabel";
import { mapErrors } from "../../../../services/config/extract-validation-errors";

import type { ApiError, SelectOption } from "../../../../services/admin-settings/type";

export type EditUserFormValues = {
  userId: string;
  email?: string;
  displayName?: string;
  securityGroups?: string[];
};

type EditUserModalProps = {
  form: FormInstance<EditUserFormValues>;
  onCancel: () => void;
  onSubmit: (values: EditUserFormValues) => void;
  open: boolean;
  securityGroupOptions: SelectOption[] | undefined;
  serverError: ApiError | null;
  isSubmitting: boolean;
};

const useStyles = createStyles(({ css, token }) => ({
  form: css`
    .ant-form-item {
      margin-bottom: ${token.marginSM}px;
    }

    .ant-form-item-row {
      align-items: center;
    }

    .ant-form-item-label {
      padding-bottom: 0;
      text-align: left;
    }

    .ant-form-item-label > label {
      height: auto;
      color: ${token.colorText};
      font-size: ${token.fontSize}px;
    }
  `,
}));

export function EditUserModal(props: EditUserModalProps) {
  const { form, isSubmitting, onCancel, onSubmit, open, securityGroupOptions, serverError } = props;
  const { t } = useTranslation();
  const { styles } = useStyles();
  const securityGroups = Form.useWatch("securityGroups", form);
  const hasNoSecurityGroups = !securityGroups || securityGroups.length === 0;

  return (
    <Modal
      testIdPrefix="ats-admin-settings-edit-user"
      title={t("platform.adminSettings.users_dialog_editUser_title")}
      open={open}
      onCancel={onCancel}
      onOk={() => form.submit()}
      afterClose={() => form.resetFields()}
      okText={t("common.domainShared.global_saveChanges")}
      cancelText={t("common.domainShared.global_cancel")}
      okButtonProps={{ loading: isSubmitting }}
      forceRender
      width={610}
    >
      <Form
        serverErrors={mapErrors(t, serverError)}
        form={form}
        labelCol={{ flex: "128px" }}
        wrapperCol={{ flex: 1 }}
        layout="horizontal"
        requiredMark={false}
        onFinish={onSubmit}
        className={styles.form}
      >
        <Form.Item testIdPrefix="ats-admin-settings-edit-user-id" name="userId" hidden />
        <Form.Item
          testIdPrefix="ats-admin-settings-edit-user-name"
          label={t("platform.adminSettings.users_dialog_addUser_name")}
          name="displayName"
          rules={[
            {
              required: true,
              message: t("platform.common.validation_required"),
            },
          ]}
        >
          <Input
            testIdPrefix="ats-admin-settings-edit-user-name-input"
            placeholder={t("platform.adminSettings.users_dialog_addUser_placeholder")}
          />
        </Form.Item>
        <Form.Item
          testIdPrefix="ats-admin-settings-edit-user-email"
          labelString={t("platform.adminSettings.users_dialog_addUser_email")}
          label={
            <TooltipLabel
              testIdPrefix="ats-admin-settings-edit-user-email"
              label={t("platform.adminSettings.users_dialog_addUser_email")}
              title={
                <>
                  <strong>
                    {t("platform.adminSettings.users_dialog_addUser_emailTooltip_title")}
                  </strong>
                  <br />
                  {t("platform.adminSettings.users_dialog_addUser_emailTooltip_content")}
                </>
              }
              icon={<InfoCircleTwoTone />}
            />
          }
          name="email"
          rules={[
            {
              required: true,
              message: t("platform.common.validation_required"),
            },
            {
              type: "email",
              // Edit trims on submit (UsersSettings.tsx), so validate the trimmed value here too;
              // otherwise a value with leading/trailing spaces fails format validation before trim ever runs.
              transform: (value: string) => value?.trim(),
              message: t("platform.common.validation_invalidEmail"),
            },
          ]}
        >
          <Input
            testIdPrefix="ats-admin-settings-edit-user-email-input"
            placeholder={t("platform.adminSettings.users_dialog_addUser_placeholder")}
          />
        </Form.Item>
        <Form.Item
          testIdPrefix="ats-admin-settings-edit-user-security-group"
          label={t("platform.adminSettings.users_columns_securityGroup_ats")}
          name="securityGroups"
          validateStatus={hasNoSecurityGroups ? "warning" : undefined}
          help={
            hasNoSecurityGroups
              ? t("platform.adminSettings.users_dialog_editUser_noSecurityGroupWarning")
              : undefined
          }
        >
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            testIdPrefix="ats-admin-settings-edit-user-security-groups"
            placeholder={t("platform.adminSettings.selectPlaceholder_securityGroups")}
            mode="multiple"
            options={securityGroupOptions}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
